import { Loader2 } from "lucide-react";
import type { BoundaryScanResult } from "../../lib/boundaryScan";
import type { FastPathReport } from "../../lib/bulkFieldFastPath";
import type {
  DropIdentification,
  IdentifiedItem,
  IdentifiedKind,
} from "./dropIdentify";

function candidateFiles(scan: BoundaryScanResult | null): Set<string> {
  return new Set(
    (scan?.candidates ?? []).map((c) => c.sourceFile.split("/")[0]!),
  );
}

function orphanPolygons(
  ident: DropIdentification | null,
  files: Set<string>,
): IdentifiedItem[] {
  return (ident?.items ?? []).filter(
    (i) => i.kind === "polygons" && !files.has(i.file),
  );
}

function itemsOf(
  ident: DropIdentification | null,
  kind: IdentifiedKind,
  orphans: IdentifiedItem[],
): IdentifiedItem[] {
  // 'polygons' marker items: when the same file produced boundary CANDIDATES the Boundaries row
  // already tells its story; otherwise the scan REJECTED the layer and the file must not vanish —
  // it shows under Unidentified as polygons-for-processing (review).
  return kind === "unidentified"
    ? [
        ...(ident?.items ?? []).filter((i) => i.kind === "unidentified"),
        ...orphans.map((i) => ({
          ...i,
          note: i.note ?? "polygons — processing will classify",
        })),
      ]
    : (ident?.items ?? []).filter((i) => i.kind === kind);
}

function namesLine(items: IdentifiedItem[]): string | null {
  if (!items.length) return null;
  return items
    .map((i) => {
      // a location claim ONLY for items whose probes were ANSWERED — an unprobed PDF must never
      // read "not inside your fields" (review, wrong-claim class).
      const where = i.fieldNames.length
        ? i.fieldNames.join(" + ")
        : i.located
          ? "not inside your fields"
          : "";
      return `${i.layer ?? i.file}${where ? ` → ${where}` : ""}${i.note ? ` (${i.note})` : ""}`;
    })
    .join(" · ");
}

function boundaryNames(
  scan: BoundaryScanResult | null,
  fastReport: FastPathReport | null,
): string {
  return fastReport
    ? [
        ...fastReport.created.map((c) => `${c.field.field_name} (created)`),
        ...fastReport.matchedExisting.map(
          (m) =>
            `${m.candidate.name} = your ${m.hits[0]?.field_name ?? "existing field"}`,
        ),
        ...fastReport.failed.map(
          (f) => `${f.candidate.name} (server will create)`,
        ),
      ].join(" · ")
    : (scan?.candidates ?? []).map((c) => c.name).join(" · ");
}

function identificationRows(
  scan: BoundaryScanResult | null,
  fastReport: FastPathReport | null,
  ident: DropIdentification | null,
): { label: string; count: number; names: string | null }[] {
  const orphans = orphanPolygons(ident, candidateFiles(scan));
  const items = (kind: IdentifiedKind) => itemsOf(ident, kind, orphans);
  const names = (kind: IdentifiedKind) => namesLine(items(kind));
  const boundaries = boundaryNames(scan, fastReport);
  return [
    {
      label: "Boundaries",
      count: scan?.candidates.length ?? 0,
      names: boundaries || null,
    },
    {
      label: "Sample points with lab results",
      count: items("samples-results").reduce((n, i) => n + i.count, 0),
      names: names("samples-results"),
    },
    {
      label: "Sample points without lab results",
      count: items("samples-noresults").reduce((n, i) => n + i.count, 0),
      names: names("samples-noresults"),
    },
    {
      label: "Yield maps",
      count: items("yield").length,
      names: names("yield"),
    },
    { label: "EC maps", count: items("ec").length, names: names("ec") },
    {
      label: "Zone sets",
      count: items("zones").length,
      names: names("zones"),
    },
    {
      label: "Unidentified files",
      count: items("unidentified").length,
      names: names("unidentified"),
    },
    // 'polygons' folded above — never its own row
  ];
}

export function IdentificationCard({
  scan,
  fastReport,
  ident,
}: {
  scan: BoundaryScanResult | null;
  fastReport: FastPathReport | null;
  ident: DropIdentification | null;
}) {
  const rows = identificationRows(scan, fastReport, ident);
  return (
    <div className="rounded border border-line px-2 py-1.5 text-[11px]">
      <div className="flex items-center gap-1.5 font-medium text-fg">
        What we identified in this drop
        {!ident && <Loader2 size={11} className="animate-spin text-faint" />}
      </div>
      <table className="mt-1 w-full">
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="align-top">
              <td className="whitespace-nowrap pr-2 text-muted">{r.label}</td>
              <td className="w-8 pr-2 text-right font-medium text-fg">
                {ident || r.label === "Boundaries" ? r.count : "…"}
              </td>
              <td className="text-[10px] text-faint">
                {r.names ?? (ident || r.label === "Boundaries" ? "—" : "")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1 text-[10px] text-faint">
        This is the in-browser read of your files — processing makes the final
        call, and anything it sees differently shows below.
        {ident?.attributionError
          ? " Field lookup was unavailable, so locations aren’t shown."
          : ""}
      </p>
    </div>
  );
}
